import { faker } from '@faker-js/faker'
import { DateTime } from 'luxon'
import { match } from 'ts-pattern'
import {
  AnalysisStateResponseSchema,
  PatchStateSchema,
  RepositoryTypeSchema,
  ToolSchema,
  type AnalysisCancellationResponse,
  type AnalysisPaginatedResponse,
  type AnalysisResponse,
  type AnalysisStateResponse,
  type AppScanIntegration,
  type AzureIntegration,
  type AzureIntegrationRepository,
  type AzurePatchResponse,
  type AzureProject,
  type BitbucketIntegration,
  type BitbucketPatchResponse,
  type BitbucketRepositoryPaginatedResponse,
  type BitbucketWorkspace,
  type BitbucketWorkspacePaginatedResponse,
  type ChainSummary,
  type CheckmarxIntegration,
  type CurrentUserResponse,
  type DatadogIntegration,
  type DimensionAnswer,
  type DimensionAnswerEvidence,
  type DimensionScore,
  type ExploitChainsResponse,
  type FindingAggregateResponse,
  type FindingOverlayCluster,
  type FindingOverlayPriority,
  type FindingOverlayResponse,
  type FindingOverlaysResponse,
  type FindingResponse,
  type FindingResultResponse,
  type GitHubIntegration,
  type GitHubPatchResponse,
  type GitLabIntegration,
  type GitLabPatchResponse,
  type GitLabProjectPaginatedResponse,
  type GitLabProjectResponse,
  type GitPatchResponse,
  type HALLink,
  type IntegrationPaginatedResponse,
  type IntegrationsBitbucketRepositoryResponse,
  type OrganizationPreferencesResponse,
  type Patch,
  type PolarisIntegration,
  type RefreshRequestResponse,
  type RefreshRequestsResponse,
  type Repository,
  type RepositoryIntegration,
  type RepositoryIntegrationPaginatedResponse,
  type RepositoryPaginatedResponse,
  type ResultsVerificationResponse,
  type Scale,
  type ScalePaginatedResponse,
  type ScanAggregateResponse,
  type ScanResponse,
  type ScorecardsResponse,
  type SonarIntegration,
  type ThreatModelResponse,
  type Tool,
  type User,
  type VeracodeIntegration,
  type WorkflowEvent,
  type WorkflowPaginatedResponse,
  type WorkflowResponse,
} from '../src/utils/api-client/api-schemas'

export type RecursivePartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[]
    ? RecursivePartial<U>[]
    : T[P] extends object | undefined
      ? RecursivePartial<T[P]>
      : T[P]
}

type Resolver<T> = (input?: RecursivePartial<T>) => T

/**
 * Maps a detector to its appropriate scale href.
 * Based on the scale-to-detector mapping used in the application.
 */
export const getScaleHrefForTool = (tool: Tool): string => {
  return match(tool)
    .with('gitlab_dependency_scanning', 'polaris_sast', 'polaris_sca', () => '/api/v1/scales/cvss')
    .with('appscan', () => '/api/v1/scales/appscan')
    .with('veracode', () => '/api/v1/scales/veracode')
    .otherwise(() => '/api/v1/scales/sonar-merged')
}

export function FakeUser(user?: Partial<User>): User {
  return {
    login: faker.internet.username(),
    name: faker.person.fullName(),
    avatar_url: `https://avatars.githubusercontent.com/u/${faker.number.int()}?v=4`,
    email: faker.internet.email(),
    ...user,
  }
}

export function FakeCurrentUser(user?: Partial<CurrentUserResponse>): CurrentUserResponse {
  return {
    login: user?.login ?? faker.internet.username(),
    username: user?.username ?? faker.internet.username(),
    avatar_url: user?.avatar_url ?? `https://avatars.githubusercontent.com/u/${faker.number.int()}?v=4`,
    email: user?.email ?? faker.internet.email(),
    // Matches a default install (see `useIsAdmin`), so an empty array is the exception a test opts
    // into, not the norm.
    roles: user?.roles ?? ['admin'],
    ...user,
  }
}

export function FakeRepository(repository: Partial<Repository> = {}): Repository {
  const repositoryName = repository.name || `${faker.word.sample()}/${faker.word.sample()}-${faker.word.sample()}`
  const repositoryType = repository.type || faker.helpers.arrayElement(RepositoryTypeSchema.options)
  repository.name = repositoryName
  repository.type = repositoryType

  const _links = {
    self: { href: 'https://github.com/test-user/repo-1' },
    'latest-analysis': { href: 'https://github.com/test-user/repo-1/latest-analysis' },
    workflows: { href: 'https://github.com/test-user/repo-1/workflows' },
    'repository-integrations': { href: 'https://github.com/test-user/repo-1/repository-integrations' },
    scans: { href: 'https://github.com/test-user/repo-1/scans' },
  } satisfies Repository['_links']

  return match(repositoryType)
    .with('github', () => {
      if (repository?.type !== 'github') {
        throw new Error('type must be github for github repositories')
      }

      return {
        type: 'github' as const,
        id: repository?.id || faker.string.uuid(),
        name: repositoryName,
        full_name: repository?.full_name || repositoryName,
        default_branch: repository?.default_branch || 'main',
        html_url: repository?.html_url || faker.internet.url(),
        owner: repository?.owner || faker.internet.username(),
        _links,
      }
    })
    .with('gitlab', () => {
      if (repository?.type !== 'gitlab') {
        throw new Error('type must be gitlab for gitlab repositories')
      }

      return {
        type: 'gitlab' as const,
        id: repository?.id || faker.string.uuid(),
        name: repositoryName,
        full_name: repository?.full_name || repositoryName,
        default_branch: repository?.default_branch || 'main',
        namespace: repository?.namespace || 'group/subgroup',
        _links,
      }
    })
    .with('azure', () => {
      if (repository?.type !== 'azure') {
        throw new Error('type must be azure for azure repositories')
      }

      const organization = repository?.organization || 'org-name'
      const project = repository?.project || 'project-name'
      const name = repository?.name || faker.word.sample()

      return {
        type: 'azure' as const,
        id: repository?.id || faker.string.uuid(),
        name,
        full_name: repository?.full_name || `${organization}/${project}/${name}`,
        default_branch: repository?.default_branch || 'main',
        organization,
        project,
        _links,
      }
    })
    .with('bitbucket', () => {
      if (repository?.type !== 'bitbucket') {
        throw new Error('type must be bitbucket for bitbucket repositories')
      }

      return {
        type: 'bitbucket' as const,
        id: repository?.id || faker.string.uuid(),
        name: repositoryName,
        full_name: repository?.full_name || repositoryName,
        default_branch: repository?.default_branch || 'main',
        workspace: repository?.workspace || 'workspace-name',
        slug: repository?.slug || repositoryName.toLowerCase().replace(/\s+/g, '-'),
        _links,
      }
    })
    .with('git', () => {
      if (repository?.type !== 'git') {
        throw new Error('type must be git for git repositories')
      }

      return {
        type: 'git' as const,
        id: repository?.id || faker.string.uuid(),
        name: repositoryName,
        full_name: repository?.full_name || repositoryName,
        default_branch: repository?.default_branch || 'main',
        _links,
      }
    })
    .exhaustive()
}

export function FakePaginatedRepositories(
  paginatedRepositories?: Partial<RepositoryPaginatedResponse>
): RepositoryPaginatedResponse {
  const selfHref = faker.internet.url()
  return {
    _links: {
      first: {
        href: selfHref,
      },
      self: {
        href: selfHref,
      },
    },
    total: 1,
    page: {
      number: 0,
      size: 10,
    },
    _embedded: {
      items: [FakeRepository()],
    },
    ...paginatedRepositories,
  }
}

export function FakeLink(link?: RecursivePartial<HALLink>): HALLink
export function FakeLink(link: undefined, { preserveUndefined }: { preserveUndefined?: true }): undefined
export function FakeLink(
  link: RecursivePartial<HALLink> | undefined,
  options?: { preserveUndefined?: true }
): HALLink | undefined
export function FakeLink(
  link: RecursivePartial<HALLink> | undefined,
  options?: { preserveUndefined?: true }
): HALLink | undefined {
  const preserveUndefined = options?.preserveUndefined === true
  if (preserveUndefined && link === undefined) {
    return undefined
  }

  return {
    href: link?.href ?? faker.internet.url(),
    title: link?.title,
  }
}

export const FakePatch = (patch?: RecursivePartial<Patch>): Patch => {
  patch = patch ?? {}
  const patchType = patch.type ?? faker.helpers.arrayElement(['git', 'github', 'gitlab', 'azure', 'bitbucket'])
  patch.type = patchType

  return match(patch.type)
    .with('git', () => {
      if (patch.type !== 'git') {
        throw new Error('type must be git for git patches')
      }

      return {
        type: 'git',
        id: patch.id ?? faker.string.uuid(),
        state: patch.state ?? faker.helpers.arrayElement(PatchStateSchema.options),
        branch_name: patch.branch_name ?? faker.git.branch(),
        _links: {
          self: { href: faker.internet.url() },
        },
      } satisfies GitPatchResponse
    })
    .with('github', () => {
      if (patch.type !== 'github') {
        throw new Error(`type must be github for github patches`)
      }

      return {
        type: 'github',
        id: patch.id ?? faker.string.uuid(),
        state: patch.state ?? faker.helpers.arrayElement(PatchStateSchema.options),
        html_url: patch.html_url ?? faker.internet.url(),
        pr_number: patch.pr_number ?? faker.number.int({ min: 1, max: 1000 }),
        merge_state: patch.merge_state,
        _links: {
          self: { href: faker.internet.url() },
        },
      } satisfies GitHubPatchResponse
    })
    .with('gitlab', () => {
      if (patch.type !== 'gitlab') {
        throw new Error(`type must be gitlab for gitlab patches`)
      }

      return {
        type: 'gitlab',
        id: patch.id ?? faker.string.uuid(),
        state: patch.state ?? faker.helpers.arrayElement(PatchStateSchema.options),
        html_url: patch.html_url ?? faker.internet.url(),
        pr_number: patch.pr_number ?? faker.number.int({ min: 1, max: 1000 }),
        merge_state: patch.merge_state,
        _links: {
          self: { href: faker.internet.url() },
        },
      } satisfies GitLabPatchResponse
    })
    .with('azure', () => {
      if (patch.type !== 'azure') {
        throw new Error(`type must be azure for azure patches`)
      }

      return {
        type: 'azure',
        id: patch.id ?? faker.string.uuid(),
        state: patch.state ?? faker.helpers.arrayElement(PatchStateSchema.options),
        html_url: patch.html_url ?? faker.internet.url(),
        pr_number: patch.pr_number ?? faker.number.int({ min: 1, max: 1000 }),
        merge_state: patch.merge_state,
        _links: {
          self: { href: faker.internet.url() },
        },
      } satisfies AzurePatchResponse
    })
    .with('bitbucket', () => {
      if (patch.type !== 'bitbucket') {
        throw new Error(`type must be bitbucket for bitbucket patches`)
      }

      return {
        type: 'bitbucket',
        id: patch.id ?? faker.string.uuid(),
        state: patch.state ?? faker.helpers.arrayElement(PatchStateSchema.options),
        html_url: patch.html_url ?? faker.internet.url(),
        pr_number: patch.pr_number ?? faker.number.int({ min: 1, max: 1000 }),
        merge_state: patch.merge_state,
        _links: {
          self: { href: faker.internet.url() },
        },
      } satisfies BitbucketPatchResponse
    })
    .exhaustive()
}

export const FakeScan = (scan?: RecursivePartial<ScanResponse>): ScanResponse => {
  const id = scan?.id ?? faker.string.uuid()
  const detector = scan?.detector ?? faker.helpers.arrayElement(ToolSchema.options)
  const scaleHref = scan?._links?.scale?.href ?? getScaleHrefForTool(detector)
  const embeddedIntegration = scan?._embedded?.integration

  return {
    id,
    html_url: faker.internet.url(),
    branch: faker.git.branch(),
    sha: faker.git.commitSha(),
    scanned_at: faker.date.recent().toISOString(),
    imported_at: faker.date.recent().toISOString(),
    ...scan,
    detector,
    _links: {
      self: FakeLink(scan?._links?.self),
      findings: FakeLink(scan?._links?.findings),
      patches: FakeLink(scan?._links?.patches),
      repository: {
        href: scan?._links?.repository?.href ?? `/api/v1/repositories/${faker.string.uuid()}`,
        title: scan?._links?.repository?.title ?? 'Fake Org / Fake Repository',
      },
      scale: { href: scaleHref, title: scan?._links?.scale?.title },
      analyses: { href: scan?._links?.analyses?.href ?? `/api/v1/analyses?scan-id=${id}` },
    },
    // Embedded integrations in tests are always tool integrations; polaris is the default shape, but
    // the caller's type wins — `scan-findings-table.tsx` renders the integration's name off it, so a
    // fixture naming `sonar` must not silently render as Polaris.
    _embedded: embeddedIntegration
      ? {
          integration: {
            type: embeddedIntegration.type ?? 'polaris',
            _links: { self: FakeLink(embeddedIntegration._links?.self) },
            id: embeddedIntegration.id,
            name: embeddedIntegration.name ?? 'Black Duck Polaris',
            source: embeddedIntegration.source ?? 'system',
            // `triage-push` is the only Capability; RecursivePartial erases the array element type, so
            // normalize element-wise. Testing the array instead would make `[]` (truthy) grant the
            // capability it is meant to withhold.
            capabilities: embeddedIntegration.capabilities?.map(() => 'triage-push' as const),
          },
        }
      : undefined,
  }
}

export const FakePaginatedScans = (paginatedScans?: Partial<ScanAggregateResponse>): ScanAggregateResponse => {
  const selfHref = faker.internet.url()
  return {
    total: paginatedScans?.total ?? paginatedScans?._embedded?.items?.length ?? 1,
    distinct_branches: paginatedScans?.distinct_branches ?? [],
    page: paginatedScans?.page ?? {
      number: 0,
      size: 10,
      ...paginatedScans?.page,
    },
    _links: {
      first: { href: selfHref },
      self: { href: selfHref },
      ...paginatedScans?._links,
    },
    _embedded: {
      items: paginatedScans?._embedded?.items ?? [FakeScan()],
    },
  }
}

export const FakeThreatModel = (threatModel?: Partial<ThreatModelResponse>): ThreatModelResponse => ({
  status: 'completed',
  phase: 'rendering',
  generatedAt: faker.date.recent().toISOString(),
  requestedBy: faker.internet.username(),
  commitSha: faker.git.commitSha(),
  failureReason: undefined,
  _links: {
    self: { href: '/api/v1/repositories/fake-repository-id/threat-model' },
    content: { href: '/api/v1/repositories/fake-repository-id/threat-model/content' },
  },
  ...threatModel,
})

export const FakeRefreshRequest = (request?: Partial<RefreshRequestResponse>): RefreshRequestResponse => {
  const id = request?.id ?? faker.string.uuid()
  const commitSha = request?.after_sha ?? faker.git.commitSha()
  return {
    id,
    state: 'completed',
    trigger_source: 'direct-push',
    triggered_at: faker.date.recent().toISOString(),
    ref: 'refs/heads/main',
    after_sha: commitSha,
    _links: {
      self: { href: `/api/v1/repositories/fake-repository-id/threat-model/refresh-requests/${id}` },
      repository: { href: '/api/v1/repositories/fake-repository-id' },
      'threat-model': { href: '/api/v1/repositories/fake-repository-id/threat-model' },
      commit: { href: `https://github.com/acme/storefront/commit/${commitSha}` },
    },
    ...request,
  }
}

export const FakeRefreshRequestList = (overrides?: Partial<RefreshRequestsResponse>): RefreshRequestsResponse => ({
  items: overrides?.items ?? [FakeRefreshRequest()],
  _links: {
    self: { href: '/api/v1/repositories/fake-repository-id/threat-model/refresh-requests' },
    repository: { href: '/api/v1/repositories/fake-repository-id' },
    'threat-model': { href: '/api/v1/repositories/fake-repository-id/threat-model' },
  },
  ...overrides,
})

export const FakeExploitChain = (chain?: Partial<ChainSummary>): ChainSummary => ({
  chainId: faker.string.uuid(),
  title: 'SQL injection to admin account takeover',
  severity: 'critical',
  linkedFindings: [
    {
      findingId: '0',
      rule: 'sql-injection',
      path: 'src/orders/lookup.py',
      kind: 'sast',
      tool: 'semgrep',
      severity: 'high',
      title: 'SQL injection in order lookup',
      fixAvailable: true,
      _links: {
        self: { href: '/api/v1/scans/scan-1/findings/0' },
        finding: { href: '/api/v1/scans/scan-1/findings/0' },
      },
    },
    {
      findingId: '1',
      rule: 'weak-jwt-secret',
      path: 'src/auth/token.py',
      kind: 'sast',
      tool: 'semgrep',
      severity: 'medium',
      title: 'Weak JWT signing secret',
      fixAvailable: false,
      _links: {
        self: { href: '/api/v1/scans/scan-1/findings/1' },
        finding: { href: '/api/v1/scans/scan-1/findings/1' },
      },
    },
  ],
  stepCount: 4,
  detailMarkdown: exampleExploitChainDetailMarkdown,
  ...chain,
})

export const FakeExploitChains = (exploitChains?: Partial<ExploitChainsResponse>): ExploitChainsResponse => ({
  status: 'completed',
  phase: 'rendering',
  generatedAt: faker.date.recent().toISOString(),
  requestedBy: faker.internet.username(),
  commitSha: faker.git.commitSha(),
  chains: [FakeExploitChain()],
  _links: {
    self: { href: '/api/v1/repositories/fake-repository-id/exploit-chains' },
    content: { href: '/api/v1/repositories/fake-repository-id/exploit-chains/content' },
  },
  ...exploitChains,
})

const fakeFindingLinks = (findingId: string) => {
  const scanId = faker.string.uuid()
  const href = `/api/v1/scans/${scanId}/findings/${encodeURIComponent(findingId)}`
  return {
    self: { href },
    finding: { href },
  }
}

export const FakeFindingOverlayPriority = (priority?: Partial<FindingOverlayPriority>): FindingOverlayPriority => {
  const findingId = priority?.findingId ?? 'codeql:js/sql-injection@src/routes/login.ts:42'
  return {
    findingId,
    tool: 'codeql',
    rule: 'sql-injection',
    path: 'src/routes/login.ts',
    severity: 'critical',
    score: 92,
    tier: 'critical',
    rationale:
      "Sits on the public 'POST /api/v1/login' entry point, reachable by the 'anonymous' actor. Touches the 'user_credentials' sensitive asset with no mitigating control between the entry point and the sink.",
    _links: fakeFindingLinks(findingId),
    ...priority,
  }
}

export const FakeFindingOverlayCluster = (cluster?: Partial<FindingOverlayCluster>): FindingOverlayCluster => ({
  name: 'Public login surface',
  dimension: 'entry_point',
  subjectName: 'POST /api/v1/login',
  rationale:
    'All three findings in this cluster are reachable from an unauthenticated caller and share the same entry point — remediating the entry-point-level input validation would deprioritize all three at once.',
  findings: [
    {
      findingId: 'codeql:js/sql-injection@src/routes/login.ts:42',
      tool: 'codeql',
      rule: 'sql-injection',
      path: 'src/routes/login.ts',
      severity: 'critical',
      _links: fakeFindingLinks('codeql:js/sql-injection@src/routes/login.ts:42'),
    },
    {
      findingId: 'codeql:js/xss@src/routes/login.ts:58',
      tool: 'codeql',
      rule: 'xss',
      path: 'src/routes/login.ts',
      severity: 'high',
      _links: fakeFindingLinks('codeql:js/xss@src/routes/login.ts:58'),
    },
  ],
  ...cluster,
})

export const FakeFindingOverlayContent = (content?: Partial<FindingOverlayResponse>): FindingOverlayResponse => ({
  narrative:
    'The concentrated risk sits on the public /api/v1/login entry point: two HIGH-severity SQL injection findings are directly reachable from anonymous callers and both touch the primary credentials store. Six additional findings on the internal /admin/* surface are behind the AdminAuthMiddleware trust boundary and are correspondingly deprioritized.',
  priorities: [FakeFindingOverlayPriority()],
  clusters: [FakeFindingOverlayCluster()],
  assumptions: [
    'Assumed the /admin/* routes are exposed only on the internal network; if any /admin/* route is reachable externally, several deprioritized findings should be revisited.',
  ],
  ...content,
})

export const FakeFindingOverlays = (findingOverlays?: Partial<FindingOverlaysResponse>): FindingOverlaysResponse => ({
  status: 'completed',
  phase: 'analyzing',
  generatedAt: faker.date.recent().toISOString(),
  requestedBy: faker.internet.username(),
  summary: {
    priorityCount: 12,
    clusterCount: 3,
    highestTier: 'critical',
  },
  _links: {
    self: { href: '/api/v1/repositories/fake-repository-id/finding-overlays' },
    content: { href: '/api/v1/repositories/fake-repository-id/finding-overlays/content' },
  },
  ...findingOverlays,
})

export const FakeDimensionAnswerEvidence = (evidence?: Partial<DimensionAnswerEvidence>): DimensionAnswerEvidence => ({
  location: 'src/api/auth.py:42',
  note: 'The login handler enforces bcrypt password verification.',
  ...evidence,
})

export const FakeDimensionAnswer = (answer?: Partial<DimensionAnswer>): DimensionAnswer => ({
  id: faker.string.uuid(),
  question: 'Does the system enforce a baseline authentication policy on all sensitive endpoints?',
  answer: 'yes',
  rationale: 'The authentication middleware guards every route under /api/v1 except health checks.',
  evidence: [FakeDimensionAnswerEvidence()],
  ...answer,
})

const EXAMPLE_DIMENSIONS: readonly Pick<DimensionScore, 'dimension' | 'displayName' | 'description'>[] = [
  {
    dimension: 'exposure-adjusted-attack-surface',
    displayName: 'Exposure-Adjusted Attack Surface',
    description: 'How big and how reachable is the surface an attacker could touch.',
  },
  {
    dimension: 'defense-in-depth-coverage',
    displayName: 'Defense in Depth',
    description: 'How consistently the system layers redundant controls behind its perimeter.',
  },
  {
    dimension: 'blast-radius-containment',
    displayName: 'Blast Radius Containment',
    description: 'How well a compromise of one component is prevented from spreading.',
  },
  {
    dimension: 'authentication-posture',
    displayName: 'Authentication Posture',
    description: 'Strength of identity verification across entry points.',
  },
  {
    dimension: 'authorization-posture',
    displayName: 'Authorization Posture',
    description: 'How consistently authenticated callers are constrained to what they are allowed to do.',
  },
  {
    dimension: 'input-handling-hygiene',
    displayName: 'Input Handling Hygiene',
    description: 'How safely untrusted data is validated, encoded, and passed to sensitive sinks.',
  },
  {
    dimension: 'secrets-and-credentials-handling',
    displayName: 'Secrets & Credentials Handling',
    description: 'How well the system stores, rotates, and gates access to secrets.',
  },
  {
    dimension: 'dependency-risk-posture',
    displayName: 'Dependency Risk Posture',
    description: 'How proactively third-party risk is tracked and mitigated.',
  },
  {
    dimension: 'transport-and-network-security',
    displayName: 'Transport & Network Security',
    description: 'How much of the data plane is protected in transit.',
  },
  {
    dimension: 'observability-and-detection',
    displayName: 'Observability & Detection',
    description: 'How quickly abuse would be noticed and investigable.',
  },
]

export const FakeDimensionScore = (score?: Partial<DimensionScore>): DimensionScore => {
  const preset = faker.helpers.arrayElement(EXAMPLE_DIMENSIONS)
  return {
    dimension: preset.dimension,
    displayName: preset.displayName,
    description: preset.description,
    score: faker.number.float({ min: 0.4, max: 0.95, fractionDigits: 2 }),
    confidence: faker.number.float({ min: 0.5, max: 0.95, fractionDigits: 2 }),
    rationale:
      'Coverage is broad across the primary entry points, but a small number of internal endpoints rely on implicit trust that would fail under a lateral-movement scenario.',
    answers: Array.from({ length: 3 }, () => FakeDimensionAnswer()),
    rubricVersion: 'v1',
    modelUsed: 'claude-opus-4-7',
    generatedAt: faker.date.recent().toISOString(),
    status: 'ok',
    ...score,
  }
}

export const FakeScorecards = (scorecards?: Partial<ScorecardsResponse>): ScorecardsResponse => ({
  status: 'completed',
  phase: 'finalizing',
  generatedAt: faker.date.recent().toISOString(),
  requestedBy: faker.internet.username(),
  commitSha: faker.git.commitSha(),
  failureReason: undefined,
  scorecard: {
    systemOverview:
      'A customer-facing storefront backed by a REST API, a relational database, and a third-party payment provider.',
    commitSha: faker.git.commitSha(),
    threatModelDocId: faker.string.uuid(),
    threatModelCommitSha: faker.git.commitSha(),
    dimensions: EXAMPLE_DIMENSIONS.map(preset => FakeDimensionScore(preset)),
    assumptionsOverhang: 0.25,
    generatedAt: faker.date.recent().toISOString(),
  },
  _links: {
    self: { href: '/api/v1/repositories/fake-repository-id/scorecards' },
  },
  ...scorecards,
})

export const exampleExploitChainDetailMarkdown = `## SQL injection to admin account takeover

\`\`\`mermaid
graph TD
  A[SQL injection in order lookup] --> B[Dump users table]
  B --> C[Recover password hashes]
  C --> D[Forge admin JWT]
\`\`\`

An attacker exploits an unparameterized order lookup to read the users table, cracks a
weak password hash, then forges an admin JWT using a guessable signing secret.
`

export const FakeScanAnalysis = (
  scanAnalysis?: RecursivePartial<AnalysisResponse>
): AnalysisResponse & { _embedded: { scan: NonNullable<NonNullable<AnalysisResponse['_embedded']>['scan']> } } => {
  const scanId = scanAnalysis?._embedded?.scan?.id ?? faker.string.uuid()
  const analysisId = scanAnalysis?.id ?? faker.string.uuid()

  // Generate realistic timestamps for state transitions
  const now = new Date()
  const baseTime = scanAnalysis?.current_state?.timestamp ? new Date(scanAnalysis.current_state.timestamp) : now

  // Define state transitions with realistic durations
  const generateLogEntries = (finalState: AnalysisStateResponse) => {
    const entries: Required<{ state: AnalysisStateResponse; timestamp: string }>[] = []
    let currentTime = new Date(baseTime)

    // Start with the earliest time (queued)
    // Add random seconds to make it more realistic
    const startMinutes = 20
    const startSeconds = faker.number.int({ min: 0, max: 59 })
    currentTime = new Date(currentTime.getTime() - (startMinutes * 60 + startSeconds) * 1000)
    entries.push({
      state: AnalysisStateResponseSchema.enum.queued,
      timestamp: currentTime.toISOString(),
    })

    // Add in_progress after 2-5 minutes plus random seconds
    const progressMinutes = faker.number.int({ min: 2, max: 5 })
    const progressSeconds = faker.number.int({ min: 0, max: 59 })
    currentTime = new Date(currentTime.getTime() + (progressMinutes * 60 + progressSeconds) * 1000)
    entries.push({
      state: AnalysisStateResponseSchema.enum['in-progress'],
      timestamp: currentTime.toISOString(),
    })

    // Add final state after 10-15 minutes plus random seconds
    if (
      finalState === AnalysisStateResponseSchema.enum.failed ||
      finalState === AnalysisStateResponseSchema.enum.completed
    ) {
      const finalMinutes = faker.number.int({ min: 10, max: 15 })
      const finalSeconds = faker.number.int({ min: 0, max: 59 })
      currentTime = new Date(currentTime.getTime() + (finalMinutes * 60 + finalSeconds) * 1000)
      entries.push({
        state: finalState,
        timestamp: currentTime.toISOString(),
      })
    }

    return entries
  }

  // Determine final state and generate log
  const finalState =
    scanAnalysis?.current_state?.state ??
    faker.helpers.arrayElement([
      AnalysisStateResponseSchema.enum.completed,
      AnalysisStateResponseSchema.enum.failed,
      AnalysisStateResponseSchema.enum['in-progress'],
    ])
  const logEntries = generateLogEntries(finalState)

  return {
    id: analysisId,
    ...scanAnalysis,
    log:
      scanAnalysis?.log && Array.isArray(scanAnalysis.log)
        ? scanAnalysis.log.map(entry => ({
            state: entry.state ?? AnalysisStateResponseSchema.enum.completed,
            timestamp: entry.timestamp ?? now.toISOString(),
          }))
        : logEntries,
    current_state: {
      state: finalState,
      timestamp: logEntries[logEntries.length - 1]?.timestamp ?? now.toISOString(),
      ...scanAnalysis?.current_state,
    },
    _links: {
      self: { href: faker.internet.url(), ...scanAnalysis?._links?.self },
      scan: { href: `/api/v1/scan/${scanId}`, ...scanAnalysis?._links?.scan },
    },
    _embedded: {
      scan: FakeScan({
        ...scanAnalysis?._embedded?.scan,
        id: scanId,
      }),
    },
  }
}

export const FakePaginatedAnalyses = (
  paginatedAnalyses?: RecursivePartial<AnalysisPaginatedResponse>
): AnalysisPaginatedResponse => {
  const resolveLinks: Resolver<AnalysisPaginatedResponse['_links']> = (links = {}) => ({
    first: {
      href: links.first?.href ?? '#',
    },
    self: {
      href: links.self?.href ?? '#',
    },
  })

  const resolvePage: Resolver<AnalysisPaginatedResponse['page']> = (page = {}) => ({
    number: page.number ?? 1,
    size: page.size ?? 1,
  })

  const resolveEmbedded: Resolver<AnalysisPaginatedResponse['_embedded']> = (embedded = {}) => ({
    items: embedded.items?.map(FakeScanAnalysis) ?? [
      FakeScanAnalysis({
        current_state: {
          state: 'completed',
          timestamp: faker.date.recent().toISOString(),
        },
      }),
    ],
  })

  return {
    _links: resolveLinks(paginatedAnalyses?._links),
    total: paginatedAnalyses?.total ?? 1,
    page: resolvePage(paginatedAnalyses?.page),
    _embedded: resolveEmbedded(paginatedAnalyses?._embedded),
  }
}

const FakeAzureProject = (project?: Partial<AzureProject>): AzureProject => {
  return {
    id: faker.string.uuid(),
    name: faker.company.name(),
    ...project,
  }
}

export const FakeAzureIntegrationRepository = (
  repository?: Partial<AzureIntegrationRepository>
): AzureIntegrationRepository => {
  return {
    id: faker.string.uuid(),
    name: faker.company.name(),
    project: FakeAzureProject(),
    ...repository,
  }
}

export const FakeCreateScanResponse = (createScanResponse?: Partial<ScanResponse>): ScanResponse => {
  return {
    _links: {
      self: FakeLink(),
      findings: FakeLink(),
      patches: FakeLink(),
      repository: FakeLink({ title: faker.company.name() }),
      scale: FakeLink(),
    },
    id: faker.string.uuid(),
    detector: faker.helpers.arrayElement(ToolSchema.options),
    sha: faker.git.commitSha(),
    scanned_at: faker.date.recent().toISOString(),
    imported_at: faker.date.recent().toISOString(),
    html_url: faker.internet.url(),
    branch: faker.git.branch(),
    ...createScanResponse,
  }
}

export function FakeWorkflow(workflow?: Partial<WorkflowResponse>): WorkflowResponse {
  const event = faker.helpers.arrayElement<WorkflowEvent>([
    { type: 'schedule', cadence: 'daily' },
    { type: 'new-scan', branch: 'main' },
    { type: 'pull-request-scan', target_branch: 'main' },
  ])
  const tool = faker.helpers.arrayElement<Tool>(['sonar', 'semgrep', 'codeql', 'snyk'])
  return {
    _links: {
      self: { href: `/api/v1/workflows/${faker.string.uuid()}` },
      repository: { href: `/api/v1/repositories/${faker.string.uuid()}` },
    },
    id: faker.string.uuid(),
    name: faker.string.sample(),
    enabled: true,
    tool,
    created_at: faker.date.recent().toISOString(),
    event,
    action: { type: 'none' },
    ...workflow,
  }
}

export function FakePaginatedWorkflows(
  paginatedWorkflows?: Partial<WorkflowPaginatedResponse>
): WorkflowPaginatedResponse {
  return {
    _links: {
      first: { href: '/api/v1/workflows' },
      self: { href: '/api/v1/workflows' },
    },
    total: 1,
    page: {
      number: 0,
      size: 10,
    },
    _embedded: {
      items: [FakeWorkflow()],
    },
    ...paginatedWorkflows,
  }
}

export const FakeGitlabProject = (gitlabProject?: Partial<GitLabProjectResponse>): GitLabProjectResponse => {
  return {
    path: faker.word.sample(),
    namespace_full_path: faker.company.name(),
    name_with_namespace: faker.company.name(),
    ...gitlabProject,
  }
}

export const FakePaginatedGitlabProjects = (
  paginatedGitlabProjects?: Partial<GitLabProjectPaginatedResponse>
): GitLabProjectPaginatedResponse => {
  const items = paginatedGitlabProjects?._embedded?.items ?? []

  const total = items.length
  const page = paginatedGitlabProjects?.page ?? {
    number: 0,
    size: items.length,
  }
  const selfHref = faker.internet.url()

  return {
    _links: {
      first: { href: selfHref },
      self: { href: selfHref },
    },
    total,
    page,
    _embedded: {
      items,
    },
    ...paginatedGitlabProjects,
  }
}

export const FakeGitHubIntegration = (integration?: Partial<GitHubIntegration>): GitHubIntegration => {
  const id = integration?.id ?? 'github-default'
  return {
    type: 'github',
    name: 'GitHub',
    source: 'system',
    app_name: `pixee-testing-testing`,
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeSonarIntegration = (integration?: Partial<SonarIntegration>): SonarIntegration => {
  // The self link is derived from the id rather than hardcoded, so overriding the id keeps the two
  // consistent — callers that match instances by HAL self link depend on that.
  const id = integration?.id ?? 'sonar-default'
  return {
    type: 'sonar',
    name: 'SonarQube',
    source: 'system',
    base_uri: 'https://sonarcloud.io',
    token_configured: true,
    webhook_secret_configured: true,
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeDatadogIntegration = (integration?: Partial<DatadogIntegration>): DatadogIntegration => {
  const id = integration?.id ?? 'datadog-default'
  return {
    type: 'datadog',
    base_uri: 'https://api.datadoghq.com',
    name: 'Production',
    // Datadog is the one type here that is genuinely user-managed and multi-instance; the rest are
    // platform-provisioned singletons, which is what `getIsSystemInstance` keys off.
    source: 'managed',
    health: { status: 'healthy' },
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeCheckmarxIntegration = (integration?: Partial<CheckmarxIntegration>): CheckmarxIntegration => {
  const id = integration?.id ?? 'checkmarx-default'
  return {
    type: 'checkmarx',
    name: 'Checkmarx',
    source: 'system',
    base_uri: 'https://checkmarx.example.com',
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeAppScanIntegration = (integration?: Partial<AppScanIntegration>): AppScanIntegration => {
  const id = integration?.id ?? 'appscan-default'
  return {
    type: 'appscan',
    name: 'AppScan',
    source: 'system',
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakePolarisIntegration = (integration?: Partial<PolarisIntegration>): PolarisIntegration => {
  const id = integration?.id ?? 'polaris-default'
  return {
    type: 'polaris',
    name: 'Black Duck Polaris',
    source: 'system',
    base_uri: 'https://polaris.blackduck.com',
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeVeracodeIntegration = (integration?: Partial<VeracodeIntegration>): VeracodeIntegration => {
  const id = integration?.id ?? 'veracode-default'
  return {
    type: 'veracode',
    name: 'Veracode',
    source: 'system',
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeAzureIntegration = (integration?: Partial<AzureIntegration>): AzureIntegration => {
  const id = integration?.id ?? 'azure-default'
  return {
    type: 'azure',
    name: 'Azure DevOps',
    source: 'system',
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeGitLabIntegration = (integration?: Partial<GitLabIntegration>): GitLabIntegration => {
  const id = integration?.id ?? 'gitlab-default'
  return {
    type: 'gitlab',
    name: 'GitLab',
    source: 'system',
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeBitbucketIntegration = (integration?: Partial<BitbucketIntegration>): BitbucketIntegration => {
  const id = integration?.id ?? 'bitbucket-default'
  return {
    type: 'bitbucket',
    name: 'Bitbucket',
    source: 'system',
    ...integration,
    id,
    _links: integration?._links ?? { self: { href: `/api/v1/integrations/${id}` } },
  }
}

export const FakeIntegrationPaginatedResponse = (
  paginatedIntegrations?: Partial<IntegrationPaginatedResponse>
): IntegrationPaginatedResponse => {
  const items = paginatedIntegrations?._embedded?.items ?? []

  return {
    _links: {
      first: { href: '/api/v1/integrations' },
      self: { href: '/api/v1/integrations' },
    },
    total: items.length,
    page: {
      number: 0,
      size: 50,
    },
    _embedded: {
      items,
    },
    ...paginatedIntegrations,
  }
}

/**
 * The `sonar-merged` scale as the platform serves it, interleaving Sonar's issue severities with
 * its hotspot severities on one rank line (`SonarScale.mergedHotspotsAndStandardExperienceScale`,
 * ranked by `RankedStandardExperienceSeverity` and `RankedHotspotSeverity`).
 *
 * The order matters more than the values: severity groups render sorted by rank descending, so a
 * fixture that reorders these teaches the wrong thing about the product. `Medium`, `High` and `Low`
 * are hotspot probabilities here, not issue severities, which is why they interleave.
 *
 * Shared with the Storybook story and the unit tests for these filters so the three cannot drift.
 */
export const SONAR_MERGED_SEVERITIES = [
  { label: 'Blocker', rank: 80 },
  { label: 'High', rank: 70 },
  { label: 'Critical', rank: 60 },
  { label: 'Medium', rank: 50 },
  { label: 'Major', rank: 40 },
  { label: 'Minor', rank: 30 },
  { label: 'Low', rank: 20 },
  { label: 'Info', rank: 10 },
]

export const FakeScale = (overrides?: Partial<Pick<Scale, 'id'>>): Scale => {
  const id = overrides?.id ?? 'sonar-merged'
  const _links = {
    self: {
      href: `/api/v1/scales/${id}`,
    },
  }

  return match(id)
    .with(
      'cvss',
      () =>
        ({
          id,
          _links,
          type: 'score',
          min: 0,
          max: 10,
        }) satisfies Scale
    )
    .otherwise(
      () =>
        ({
          id,
          _links,
          type: 'ranked-set',
          severities: SONAR_MERGED_SEVERITIES,
        }) satisfies Scale
    )
}

export const FakePaginatedScales = (overrides?: Partial<ScalePaginatedResponse>): ScalePaginatedResponse => ({
  _links: {
    first: {
      href: '/api/v1/scales',
    },
    self: {
      href: '/api/v1/scales',
    },
  },
  total: 4,
  page: {
    number: 0,
    size: 10,
  },
  _embedded: {
    items: [FakeScale()],
  },
  ...overrides,
})

export const FakeRepositoryIntegration = (integration?: Partial<RepositoryIntegration>): RepositoryIntegration => {
  if (!integration) {
    return {
      type: 'sonar' as const,
      id: faker.string.uuid(),
      html_url: faker.internet.url(),
      project_key: faker.string.alphanumeric(8),
      _links: {
        self: { href: faker.internet.url() },
      },
    }
  }
  return (
    match(integration)
      .with({ type: 'sonar' }, ({ type, id, html_url, project_key }) => ({
        type,
        id: id ?? faker.string.uuid(),
        html_url: html_url ?? faker.internet.url(),
        project_key: project_key ?? faker.string.alphanumeric(8),
        _links: {
          self: { href: faker.internet.url() },
        },
      }))
      .with({ type: 'veracode' }, ({ type, id }) => ({
        type,
        id: id ?? faker.string.uuid(),
        application_guid: faker.string.uuid(),
        _links: {
          self: { href: faker.internet.url() },
        },
      }))
      .with({ type: 'appscan' }, ({ type, id, application_id, html_url }) => ({
        type,
        id: id ?? faker.string.uuid(),
        html_url: html_url ?? faker.internet.url(),
        application_id: application_id ?? faker.string.alphanumeric(8),
        _links: {
          self: { href: faker.internet.url() },
        },
      }))
      .with({ type: 'checkmarx' }, ({ type, id, project_id, html_url }) => ({
        type,
        id: id ?? faker.string.uuid(),
        html_url: html_url ?? faker.internet.url(),
        project_id: project_id ?? faker.string.alphanumeric(8),
        _links: {
          self: { href: faker.internet.url() },
        },
      }))
      .with({ type: 'arnica' }, ({ type, id, asset_key, provenance, created_at, updated_at }) => ({
        type,
        id: id ?? faker.string.uuid(),
        asset_key: asset_key ?? faker.string.alphanumeric(8),
        provenance: provenance ?? 'pattern-matching',
        created_at: created_at ?? faker.date.past().toISOString(),
        updated_at: updated_at ?? faker.date.past().toISOString(),
        _links: {
          self: { href: faker.internet.url() },
        },
      }))
      // TODO: Add support for other types.
      .otherwise(() => ({
        type: 'sonar' as const,
        id: faker.string.uuid(),
        html_url: faker.internet.url(),
        project_key: faker.string.alphanumeric(8),
        _links: {
          self: { href: faker.internet.url() },
        },
      }))
  )
}

export const FakePaginatedRepositoryIntegrations = (
  paginatedIntegrations?: Partial<RepositoryIntegrationPaginatedResponse>
): RepositoryIntegrationPaginatedResponse => {
  const selfHref = faker.internet.url()
  return {
    _links: {
      first: {
        href: selfHref,
      },
      self: {
        href: selfHref,
      },
    },
    total: 1,
    page: {
      number: 0,
      size: 10,
    },
    _embedded: {
      items: [
        FakeRepositoryIntegration({
          type: 'sonar',
          project_key: 'pixee2_webgoat',
        }),
      ],
    },
    ...paginatedIntegrations,
  }
}

export const FakeBitbucketWorkspace = (workspace?: Partial<BitbucketWorkspace>): BitbucketWorkspace => ({
  slug: faker.string.uuid(),
  name: faker.company.name(),
  _links: {
    repositories: { href: '/api/v1/integrations/bitbucket-default/workspaces/pixee/repositories' },
  },
  ...workspace,
})

export const FakePaginatedBitbucketWorkspaces = (
  paginatedBitbucketWorkspaces?: Partial<BitbucketWorkspacePaginatedResponse>
): BitbucketWorkspacePaginatedResponse => ({
  _links: {
    first: { href: '/api/v1/integrations/bitbucket-default/workspaces' },
    self: { href: '/api/v1/integrations/bitbucket-default/workspaces' },
  },
  total: 1,
  page: { number: 0, size: 10 },
  _embedded: { items: [] },
  ...paginatedBitbucketWorkspaces,
})

export const FakeBitbucketIntegrationRepository = (
  repository?: Partial<IntegrationsBitbucketRepositoryResponse>
): IntegrationsBitbucketRepositoryResponse => ({
  name: faker.company.name(),
  slug: faker.string.uuid(),
  workspace: faker.company.name(),
  ...repository,
})

export const FakePaginatedBitbucketRepositories = (
  paginatedBitbucketRepositories?: Partial<BitbucketRepositoryPaginatedResponse>
): BitbucketRepositoryPaginatedResponse => ({
  _links: {
    first: { href: '/api/v1/integrations/bitbucket-default/workspaces/pixee/repositories' },
    self: { href: '/api/v1/integrations/bitbucket-default/workspaces/pixee/repositories' },
  },
  total: 1,
  page: { number: 0, size: 10 },
  _embedded: { items: [] },
  ...paginatedBitbucketRepositories,
})

export const FakeResultsVerificationResponse = (
  verification?: Partial<ResultsVerificationResponse>
): ResultsVerificationResponse => ({
  result: 'not-exploitable',
  headlines: [
    {
      title: 'Path Element(s) Controlled',
      description: 'DIRECTORY',
    },
  ],
  body: '## Findings\n\nThe verification process confirmed this finding.',
  code_blocks: [
    {
      title: 'Proof-of-Concept',
      filename: 'poc.js',
      content: 'console.log("test");',
    },
  ],
  payload_results: {
    columns: ['Payload', 'Output', 'Return Value', 'Had Error'],
    rows: [
      {
        Payload: '../../../etc/passwd',
        Output: 'STDERR: Invalid version argument. Exiting.',
        'Return Value': '1',
        'Had Error': 'True',
      },
      {
        Payload: '1.2.3-..-..-etc-passwd',
        Output:
          'STDOUT: Final file path constructed: /service/versions/v1/releases/1.2.3-..-..-etc-passwd/manifest.json',
        'Return Value': '0',
        'Had Error': 'False',
      },
      {
        Payload: 'C:\\windows\\system32\\config\\sam',
        Output: 'STDERR: Invalid version argument. Exiting.',
        'Return Value': '1',
        'Had Error': 'True',
      },
    ],
  },
  ...verification,
})

export const FakePaginatedFindingsWithResults = (
  paginatedFindingsWithResults?: RecursivePartial<FindingAggregateResponse>
): FindingAggregateResponse => {
  const resolveLinks: Resolver<FindingAggregateResponse['_links']> = (links = {}) => ({
    first: {
      href: links.first?.href ?? '/api/v1/scans/scan-id/findings',
    },
    self: {
      href: links.self?.href ?? '/api/v1/scans/scan-id/findings',
    },
  })

  const resolvePage: Resolver<FindingAggregateResponse['page']> = (page = {}) => ({
    number: page.number ?? 0,
    size: page.size ?? 10,
  })

  const resolveEmbedded: Resolver<FindingAggregateResponse['_embedded']> = (embedded = {}) => ({
    items: embedded.items?.map(item => FakeFindingWithResults(item)) ?? [FakeFindingWithResults()],
  })

  const resolveTriage: Resolver<FindingAggregateResponse['triage']> = (triage = {}) => ({
    status: {
      completed: triage.status?.completed ?? 45,
      failed: triage.status?.failed ?? 5,
      no_recommendations: triage.status?.no_recommendations ?? 10,
      in_progress: triage.status?.in_progress ?? 0,
    },
    outcome: {
      true_positive: triage.outcome?.true_positive ?? 20,
      likely_true_positive: triage.outcome?.likely_true_positive ?? 10,
      inconclusive: triage.outcome?.inconclusive ?? 5,
      false_positive: triage.outcome?.false_positive ?? 8,
      wont_fix: triage.outcome?.wont_fix ?? 2,
      blocked: triage.outcome?.blocked ?? 0,
      excluded: triage.outcome?.excluded ?? 0,
      not_triaged: triage.outcome?.not_triaged ?? 0,
    },
  })

  const resolveFix: Resolver<FindingAggregateResponse['fix']> = (fix = {}) => ({
    status: {
      completed: fix.status?.completed ?? 30,
      failed: fix.status?.failed ?? 3,
      no_recommendations: fix.status?.no_recommendations ?? 5,
      in_progress: fix.status?.in_progress ?? 0,
    },
    outcome: {
      has_fix: fix.outcome?.has_fix ?? 25,
      blocked: fix.outcome?.blocked ?? 3,
      excluded: fix.outcome?.excluded ?? 2,
    },
    confidence: {
      high: fix.confidence?.high ?? 15,
      medium: fix.confidence?.medium ?? 8,
      low: fix.confidence?.low ?? 2,
      no_rating: fix.confidence?.no_rating ?? 0,
    },
  })

  const resolveSca: Resolver<FindingAggregateResponse['sca']> = (sca = {}) => ({
    status: {
      completed: sca.status?.completed ?? 0,
      failed: sca.status?.failed ?? 0,
      blocked: sca.status?.blocked ?? 0,
      excluded: sca.status?.excluded ?? 0,
      in_progress: sca.status?.in_progress ?? 0,
    },
    classification: {
      exploitable: sca.classification?.exploitable ?? 0,
      not_exploitable: sca.classification?.not_exploitable ?? 0,
      inconclusive: sca.classification?.inconclusive ?? 0,
    },
  })

  const resolveComposite: Resolver<FindingAggregateResponse['composite']> = (composite = {}) => ({
    ready_to_fix: composite.ready_to_fix ?? 0,
    no_fix_necessary: composite.no_fix_necessary ?? 0,
  })

  const total = paginatedFindingsWithResults?.total ?? paginatedFindingsWithResults?._embedded?.items?.length ?? 1

  return {
    _links: resolveLinks(paginatedFindingsWithResults?._links),
    total,
    page: resolvePage(paginatedFindingsWithResults?.page),
    _embedded: resolveEmbedded(paginatedFindingsWithResults?._embedded),
    completed_analysis: paginatedFindingsWithResults?.completed_analysis ?? 60,
    in_progress_analysis: paginatedFindingsWithResults?.in_progress_analysis ?? 0,
    triage: resolveTriage(paginatedFindingsWithResults?.triage),
    fix: resolveFix(paginatedFindingsWithResults?.fix),
    sca: resolveSca(paginatedFindingsWithResults?.sca),
    composite: resolveComposite(paginatedFindingsWithResults?.composite),
  }
}

export const FakeFindingWithResults = (findingWithResults?: RecursivePartial<FindingResponse>): FindingResponse => {
  const findingId = findingWithResults?.id ?? '00000000-0000-0000-0000-000000000000'

  return {
    id: findingId,
    title: findingWithResults?.title ?? 'Sample finding title',
    rule: findingWithResults?.rule ?? 'sample-rule',
    severity: {
      label: findingWithResults?.severity?.label ?? 'medium',
      rank: findingWithResults?.severity?.rank ?? 6.0,
    },
    html_url: findingWithResults?.html_url ?? 'https://example.com/finding',
    push_state: findingWithResults?.push_state,
    _links: {
      self: {
        href: findingWithResults?._links?.self?.href ?? `/api/v1/scans/scan-id/findings/${findingId}`,
      },
      results: {
        href: findingWithResults?._links?.results?.href ?? `/api/v1/scans/scan-id/findings/${findingId}/results`,
      },
      'representative-results': {
        href:
          findingWithResults?._links?.['representative-results']?.href ??
          `/api/v1/scans/scan-id/findings/${findingId}/results?representative=true`,
      },
    },
    _embedded: {
      'representative-results': FakeResults(findingWithResults?._embedded?.['representative-results']),
      ...(findingWithResults?._embedded?.results && {
        results: FakeResults(findingWithResults._embedded.results),
      }),
    },
  }
}

export const FakeResults = (
  representativeResults?: RecursivePartial<NonNullable<FindingResponse['_embedded']>['representative-results']>
): NonNullable<FindingResponse['_embedded']>['representative-results'] => {
  // Partials flow through `FakeResults._embedded.items` from legacy story fixtures and are passed
  // through to `FakeResult`; widen here so the FakeResult input check stays meaningful for direct callers.
  const items = representativeResults?._embedded?.items?.map(item => FakeResult(item as FakeResultInput)) ?? []

  const selfHref =
    representativeResults?._links?.self?.href ?? `/api/v1/scans/scan-id/findings/1/results?representative=true`
  return {
    _links: {
      first: {
        href: representativeResults?._links?.first?.href ?? selfHref,
      },
      self: {
        href: selfHref,
      },
      finding: {
        href: representativeResults?._links?.finding?.href ?? '#',
      },
    },
    _embedded: {
      items,
    },
    page: {
      number: representativeResults?.page?.number ?? 0,
      size: representativeResults?.page?.size ?? 10,
    },
    total: representativeResults?.total ?? items.length,
  }
}

// Distributive over the FindingResultResponse union so each variant keeps its own per-variant `_links`
// and `_embedded` shapes. `outcome` and (on triage) `latest_push_state`, when supplied, must be full
// values of the matching variant — a recursively-partial one would silently produce a malformed fixture.
type FakeResultInput = FindingResultResponse extends infer R
  ? R extends FindingResultResponse
    ? Omit<RecursivePartial<R>, 'outcome' | 'latest_push_state'> & {
        outcome?: R['outcome']
      } & ('latest_push_state' extends keyof R ? { latest_push_state?: R['latest_push_state'] } : object)
    : never
  : never

export const FakeResult = (result?: FakeResultInput): FindingResultResponse => {
  return match(result)
    .with(
      { type: 'triage' },
      result =>
        ({
          id: result.id ?? 'result-id-placeholder',
          type: result.type,
          completed: result.completed ?? DateTime.now().toISO(),
          // Synthesize a default outcome only when the caller omits the key. An explicit
          // `outcome: undefined` is preserved so callers can model an in-progress (not-yet-completed)
          // result — the backend represents that by omitting `outcome`, never by sending null.
          outcome:
            'outcome' in result
              ? result.outcome
              : ({
                  type: 'completed-triage',
                  suggested_status: 'true_positive',
                  suggested_severity: { label: 'critical', rank: 9.0 },
                  summary: 'This SQL injection vulnerability allows arbitrary database access...',
                } satisfies Extract<FindingResultResponse, { type: 'triage' }>['outcome']),
          latest_push_state: result.latest_push_state,
          _links: {
            self: { href: result._links?.self?.href ?? 'https://example.com/result' },
            finding: { href: result._links?.finding?.href ?? `/api/v1/scans/scan-id/findings/1` },
            analysis: { href: result._links?.analysis?.href ?? 'https://example.com/analysis' },
            article: result._links?.article?.href === undefined ? undefined : { href: result._links.article.href },
            report: result._links?.report?.href === undefined ? undefined : { href: result._links.report.href },
            verification:
              result._links?.verification?.href === undefined ? undefined : { href: result._links.verification.href },
          },
        }) satisfies FindingResultResponse
    )
    .with({ type: 'fix' }, result => {
      return {
        id: result.id ?? 'result-id-placeholder',
        type: result.type,
        completed: result.completed ?? DateTime.now().toISO(),
        outcome: result.outcome,
        _links: {
          self: { href: result._links?.self?.href ?? 'https://example.com/result' },
          finding: { href: result._links?.finding?.href ?? `/api/v1/scans/scan-id/findings/1` },
          analysis: { href: result._links?.analysis?.href ?? 'https://example.com/analysis' },
          changesets: { href: result._links?.changesets?.href ?? 'https://example.com/changesets' },
          patches: { href: result._links?.patches?.href ?? 'https://example.com/patches' },
          'latest-patch': { href: result._links?.patches?.href ?? 'https://example.com/latest-patch' },
        },
        // Preserve _embedded if provided (for latest-patch support)
        ...(result._embedded?.['latest-patch'] && {
          _embedded: {
            'latest-patch': {
              _links: {
                first: {
                  href:
                    result._embedded['latest-patch']._links?.first?.href ??
                    result._embedded['latest-patch']._links?.self?.href ??
                    'https://example.com/latest-patch-collection',
                },
                self: {
                  href:
                    result._embedded['latest-patch']._links?.self?.href ??
                    'https://example.com/latest-patch-collection',
                },
              },
              total: result._embedded['latest-patch'].total ?? 0,
              page: {
                number: result._embedded['latest-patch'].page?.number ?? 0,
                size: result._embedded['latest-patch'].page?.size ?? 10,
              },
              _embedded: {
                items: result._embedded['latest-patch']._embedded?.items?.map(FakePatch) ?? [],
              },
            },
          },
        }),
      } satisfies FindingResultResponse
    })
    .with(
      { type: 'sca' },
      result =>
        ({
          id: result.id ?? 'result-id-placeholder',
          type: result.type,
          completed: result.completed ?? DateTime.now().toISO(),
          outcome: result.outcome,
          _links: {
            self: { href: result._links?.self?.href ?? 'https://example.com/result' },
            finding: { href: result._links?.finding?.href ?? `/api/v1/scans/scan-id/findings/1` },
            analysis: { href: result._links?.analysis?.href ?? 'https://example.com/analysis' },
          },
        }) satisfies FindingResultResponse
    )
    .otherwise(
      () =>
        ({
          id: 'result-id-placeholder',
          type: 'triage',
          completed: DateTime.now().toISO(),
          outcome: {
            type: 'completed-triage',
            suggested_status: 'true_positive',
            suggested_severity: { label: 'critical', rank: 9.0 },
            summary: 'This SQL injection vulnerability allows arbitrary database access...',
          },
          _links: {
            self: { href: 'https://example.com/result' },
            finding: { href: `/api/v1/scans/scan-id/findings/1` },
            analysis: { href: 'https://example.com/analysis' },
          },
        }) satisfies FindingResultResponse
    )
}

export const FakeAnalysisCancellation = (
  cancellation?: Partial<AnalysisCancellationResponse>
): AnalysisCancellationResponse => {
  const id = cancellation?.id ?? faker.string.uuid()
  return {
    id,
    created_at: cancellation?.created_at ?? new Date().toISOString(),
    created_by: cancellation?.created_by ?? faker.internet.email(),
    _links: {
      self: { href: `/api/v1/analysis-cancellations/${id}` },
      scan: { href: '/api/v1/scans/fake-scan-id' },
      analyses: { href: `/api/v1/analysis-cancellations/${id}/analyses` },
      ...cancellation?._links,
    },
  }
}

export function FakeOrganizationPreferences(
  preferences?: Partial<OrganizationPreferencesResponse>
): OrganizationPreferencesResponse {
  const orgId = preferences?.org_id ?? 'default-organization'
  return {
    content: preferences?.content ?? faker.lorem.paragraph(),
    updated_at: preferences?.updated_at ?? '2024-01-01T00:00:00Z',
    updated_by: preferences?.updated_by ?? faker.internet.email(),
    org_id: orgId,
    ...preferences,
    _links: {
      self: { href: `/api/v1/organizations/${orgId}/preferences` },
      ...preferences?._links,
    },
  }
}
